import { useContext, useCallback, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { TemplatePrompts } from '@klab-berlin/api-sdk/lib/types/common/Conversation';
import { aiAssistantContext } from '../../../state/AIAssistantContextProvider';
import { ActiveView } from './constants';
import { isImageGenerationTemplate } from './utils';
import { getAiGeneratedImagesRemainingLimit } from '../../../reducers/aiAssistant.selectors';
import {
  getGeneratedImagesRemainingLimit as getGeneratedImagesRemainingLimitAction,
} from '../../../actions/aiAssistant.actions';
import useAction from '../../../utils/useAction';

/**
 * Shared hook that encapsulates the template prompt selection logic
 * used by both AIAssistantFreeDraftEntry (dialog) and AIToolsPageContent (page).
 */
const useTemplateSelection = () => {
  const {
    setActiveView,
    setSelectedTemplatePromptId,
    setImageGenerationLimitReachedAlertVisible,
    isPromptLimitReached,
    showPromptLimitAlert,
  } = useContext(aiAssistantContext);

  const remainingLimit = useSelector(getAiGeneratedImagesRemainingLimit);
  const hasRemainingLimit = !!remainingLimit && remainingLimit > 0;
  const fetchRemainingLimit = useAction(getGeneratedImagesRemainingLimitAction);

  useEffect(() => {
    fetchRemainingLimit();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleTemplateClick = useCallback(
    (_title: string, _subheader: string, id: TemplatePrompts) => {
      if (isPromptLimitReached) {
        showPromptLimitAlert();
        return;
      }

      if (isImageGenerationTemplate(id) && !hasRemainingLimit) {
        setImageGenerationLimitReachedAlertVisible(true);
        return;
      }

      setSelectedTemplatePromptId(id);
      setActiveView(ActiveView.TEMPLATE_PROMPT_FORM);
    },
    [
      setActiveView, setSelectedTemplatePromptId, hasRemainingLimit,
      setImageGenerationLimitReachedAlertVisible, isPromptLimitReached, showPromptLimitAlert,
    ]
  );

  return { handleTemplateClick };
};

export default useTemplateSelection;
